import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { useCapitalGlobalCoveragesCatalog } from '@/features/quotes/hooks/capital-global/use-capital-global-coverages-catalog.hook';
import { findMorteCoverage } from '@/features/quotes/schemas/capital-global/coverages-catalog';
import type { StepCoveragesFormValues } from '@/features/quotes/schemas/capital-global/step-coverages.schema';
import { FUNERAL_ASSIST_SELECT_ENABLED } from '@/features/quotes/schemas/capital-global/step-coverages.schema';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import { AssistanceFuneralSelectSection } from './sections/assistance-funeral-select-section.component';
import { AssistanceToggles } from './sections/assistance-toggles.component';
import { CapitalizationSelectSection } from './sections/capitalization-select-section.component';
import { CoverageErrorBanner } from './sections/coverage-error-banner.component';
import { CoveragesTable } from './sections/coverages-table.component';
import { EntityRiskTabs } from './sections/step-dados-do-risco.sections';
import { useStepCoveragesEntityTabs } from './hooks/use-step-coverages-entity-tabs.hook';
import { useStepCoveragesViewModel } from './hooks/use-step-coverages-view-model.hook';
import { groupCatalogByCategory } from './utils/group-catalog-by-category.util';

const formOpts = formOptions({
  defaultValues: {
    coverages: {} as StepCoveragesFormValues['coverages'],
    assistances: {} as StepCoveragesFormValues['assistances'],
    capitalization: '',
    assistanceFuneral: '',
  },
});

type Props = {
  onNext: () => void;
  onBack: () => void;
  canGoNext?: boolean;
  canGoBack?: boolean;
  isViewMode?: boolean;
};

export function StepCoverages({
  onNext,
  onBack,
  canGoNext = true,
  canGoBack = true,
  isViewMode = false,
}: Props) {
  const form = useQuoteForm({ ...formOpts });

  // Load catalog at component level so entity-tabs hook can use it for prefill.
  const {
    coverageCatalog,
    assistanceCatalog,
    assistanceUmbrella,
    capitalizationUmbrella,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
  } = useCapitalGlobalCoveragesCatalog();

  const morteCatalogItem = useMemo(
    () => findMorteCoverage(coverageCatalog),
    [coverageCatalog]
  );
  const morteId = morteCatalogItem?.id;

  const {
    entities,
    hasMultipleEntities,
    activeEntityId,
    referenceCapital,
    handleTabChange,
    captureAllEntities,
    applyEntityValues,
  } = useStepCoveragesEntityTabs({
    form,
    coverageCatalog,
    assistanceCatalog,
    assistanceUmbrella,
    capitalizationUmbrella,
    morteId,
    isCatalogLoading,
    isCatalogError,
  });

  const {
    coverageCatalog: visibleCoverageCatalog,
    assistanceCatalog: visibleAssistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    isCapitalizationAvailable,
    retryCatalog,
    handleToggleCoverage,
    handleIndividualCapitalChange,
    handleBenefitChange,
    handlePercentChange,
    handlePercentBlur,
    handleToggleAssistance,
    handleCapitalizationChange,
    handleAssistanceFuneralChange,
    handleSubmit,
    handleEntityTabChangeWithReset,
    isPending,
    getVisibleErrors,
  } = useStepCoveragesViewModel({
    form,
    onNext,
    referenceCapital,
    activeEntityId,
    entities,
    handleEntityTabChange: handleTabChange,
    captureAllEntities,
    applyEntityValues,
  });

  // Uma seção por categoria oficial do catálogo (CoverageCategory_CodeDesc),
  // na ordem de aparição — mesma regra do Step Plano do PME.
  const coverageCategorySections = useMemo(
    () => groupCatalogByCategory(visibleCoverageCatalog),
    [visibleCoverageCatalog]
  );

  if (isCatalogLoading) {
    return (
      <div className='flex flex-col gap-2 px-6 py-8'>
        <Text className='text-base font-semibold text-alba-black-500'>
          Carregando catálogo de coberturas e assistências...
        </Text>
      </div>
    );
  }

  if (isCatalogError) {
    return (
      <div className='flex flex-col gap-4 px-6 py-8'>
        <Text className='text-base font-semibold text-red-600'>
          Não foi possível carregar o catálogo deste produto.
        </Text>
        <div>
          <Button variant='secondary' size='md' onClick={retryCatalog}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form.Subscribe
      selector={state => state.values}
      children={values => {
        const {
          coverageErrors,
          assistanceErrors,
          capitalizationError,
          assistanceFuneralError,
          generalError,
        } = getVisibleErrors(values);

        const coverageContent = (
          <div className='flex flex-col gap-8'>
            <CoverageErrorBanner messages={[generalError]} />
            {coverageCategorySections.map(section => (
              <div key={section.category} className='flex flex-col gap-6'>
                <CoveragesTable
                  title={section.category}
                  catalog={section.items}
                  states={values.coverages}
                  errors={coverageErrors}
                  onToggle={handleToggleCoverage}
                  onIndividualCapitalChange={handleIndividualCapitalChange}
                  onBenefitChange={handleBenefitChange}
                  onPercentChange={handlePercentChange}
                  onPercentBlur={handlePercentBlur}
                  disabled={isViewMode}
                  morteId={morteId}
                />
              </div>
            ))}

            <div className='flex flex-col gap-6'>
              <Text className='text-lg font-bold text-alba-black-500'>
                Assistências
              </Text>
              <AssistanceToggles
                assistances={visibleAssistanceCatalog}
                states={values.assistances}
                onToggle={handleToggleAssistance}
                disabled={isViewMode}
                errors={assistanceErrors}
                bottomSlot={
                  FUNERAL_ASSIST_SELECT_ENABLED ? (
                    <AssistanceFuneralSelectSection
                      value={values.assistanceFuneral}
                      onValueChange={handleAssistanceFuneralChange}
                      disabled={isViewMode}
                      error={assistanceFuneralError}
                      items={assistanceFuneralOptions}
                    />
                  ) : null
                }
              />
            </div>

            {/* Capitalização exige sócios: some quando a entidade só tem colaboradores. */}
            {isCapitalizationAvailable ? (
              <CapitalizationSelectSection
                value={values.capitalization}
                onValueChange={handleCapitalizationChange}
                disabled={isViewMode}
                error={capitalizationError}
                items={capitalizationOptions}
              />
            ) : null}
          </div>
        );

        return (
          <div className='flex flex-col gap-8'>
            {hasMultipleEntities ? (
              <EntityRiskTabs
                entities={entities}
                activeEntityId={activeEntityId}
                onTabChange={handleEntityTabChangeWithReset}
              >
                {coverageContent}
              </EntityRiskTabs>
            ) : (
              coverageContent
            )}

            <div className='relative flex flex-row justify-between items-center px-6 gap-6 self-stretch'>
              <Button
                variant='ghost'
                size='lg'
                className='flex items-center gap-2'
                onClick={onBack}
                disabled={isViewMode || !canGoBack}
              >
                <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
                Voltar
              </Button>
              <Button
                variant='primary'
                size='md'
                className='flex items-center gap-2 px-8 py-2 rounded-xl'
                onClick={handleSubmit}
                disabled={isViewMode || !canGoNext || isPending}
              >
                {isPending ? (
                  <Spinner size='sm' />
                ) : (
                  <>
                    Próximo
                    <ArrowRight className='w-6 h-6' strokeWidth={2} />
                  </>
                )}
              </Button>
            </div>
          </div>
        );
      }}
    />
  );
}
